
import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase/client';
import {
  X, Save, Loader2, Calendar, Clock, Building2, Users, UserCircle, Search, Mail
} from 'lucide-react';
import type { Termin, TerminTeilnehmer } from './KalenderView';
import { lokalZuIso, isoZuLokalDatum, isoZuLokalZeit } from '../../../lib/kalender/zeit';
import { sucheEmpfaenger } from '../../../lib/kontakte/empfaengerSuche';
import { anfrageBetreff, anfrageText, terminAnfragePlatzhalter } from '../../../lib/termine/anfrage';
import { useIslandActions } from '../../../components/island/IslandProvider';
import EmailVerfassen from '../emails/EmailVerfassen';
import {
  ladeTicketZuTermin, protokolliereTermin, terminZeitText, verknuepfeTicketTermin,
} from '../../../lib/tickets/terminLink';
import {
  Buildings, CalendarBlank, CalendarPlus, Check as PhCheck, Clock as PhClock, DotsThreeCircle, EnvelopeSimple, Eye, FloppyDisk,
  Globe, HourglassMedium, LinkSimple, Minus, Question, Sun, TextAlignLeft, User, UserCircle as PhUserCircle, UsersThree, Wrench, X as PhX,
} from '@phosphor-icons/react';
import { FormRahmen } from '../../../components/island/form/FormRahmen';
import { FormAbschnitt } from '../../../components/island/form/FormAbschnitt';
import { FormFeld, FormZeile } from '../../../components/island/form/FormFeld';
import { FormSegment } from '../../../components/island/form/FormSegment';
import { FormAuswahl } from '../../../components/island/form/FormAuswahl';
import { FormSuche } from '../../../components/island/form/FormSuche';
import { FormSchalter } from '../../../components/island/form/FormSchalter';
import { FormChips } from '../../../components/island/form/FormChips';
import { FormKleinKnopf } from '../../../components/island/form/FormKleinKnopf';
import { FormKnopfleiste } from '../../../components/island/form/FormKnopfleiste';
import { useFormFehler } from '../../../components/island/form/useFormFehler';
import { ABSCHNITT_FARBE, CHIP, FORM, ZEILEN_ICON } from '../../../components/island/form/form-stil';
import { MAIL_FORM_META } from '../../../components/island/form/mailMeta';

interface TerminFormularProps {
  editTermin: Termin | null;
  prefillDate: string | null;
  teamMembers: { id: string; full_name: string }[];
  userProfile?: any;
  onClose: () => void;
  onSaved: () => void;
  /**
   * Aus einem Ticket heraus angelegt: Der neue Termin wird mit dem Ticket verknüpft und
   * die Anlage im Ticket-Verlauf festgehalten. Beim Bearbeiten wird die Verknüpfung
   * ohnehin nachgeschlagen — Änderungen landen dann auch ohne diese Prop im Verlauf.
   */
  ticketId?: string;
  /** Vorbelegung beim Anlegen aus einem Ticket (Objekt und Titel des Vorgangs). */
  prefillObjektId?: string | null;
  prefillTitel?: string | null;
  /** Beschreibung vorbelegen (z. B. #570: Absender und Auszug der Mail, aus der der Termin entsteht). */
  prefillBeschreibung?: string | null;
  /** Ende vorbelegen ('YYYY-MM-DDTHH:MM', z. B. #570: Zeitfenster „14–18 Uhr“ aus einer Mail). */
  prefillEnde?: string | null;
  /** Vorbelegter Typ beim Anlegen (z. B. 'wartung' aus dem Objekt-Panel „Wartung“). */
  prefillTyp?: string | null;
  /** Mieter vorbelegen (z. B. Übergabe aus der Mieterseite) */
  prefillMieterId?: string | null;
  /** Eigentümer vorbelegen (z. B. aus der Eigentümerseite) */
  prefillEigentuemerId?: string | null;
  /** Dienstleister verknüpfen (termine.dienstleister_id, z. B. aus der Dienstleisterseite) */
  prefillDienstleisterId?: string | null;
  /**
   * 'pille' rendert ohne Overlay/eigenen Header für den Island-Formularbereich
   * (dunkles Theme — die Pille liefert Titelzeile + Schließen), wie TicketFormular.
   */
  /** 'immo' = neues Design (Immobilienbereich, Kalender): Design-Tokens, inline statt Pille/Modal. */
  variant?: 'modal' | 'pille' | 'immo';
}

const TYP_OPTIONS = [
  { value: 'besichtigung', label: 'Besichtigung' },
  { value: 'wartung', label: 'Wartung' },
  { value: 'frist', label: 'Frist' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'sonstig', label: 'Sonstiges' },
];

const STATUS_OPTIONS = [
  { value: 'geplant', label: 'Geplant' },
  { value: 'abgeschlossen', label: 'Abgeschlossen' },
  { value: 'abgesagt', label: 'Abgesagt' },
];

// Klassen-Paletten je Variante (hell = zentriertes Modal, dunkel = Pille)
const LABEL_MODAL = 'text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1.5 block';
const LABEL_PILLE = 'text-[9px] font-black text-white/40 uppercase tracking-[0.2em] mb-1.5 block';
const INPUT_MODAL = 'w-full px-4 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/30 focus:border-[#c4a77d] transition-all';
const INPUT_PILLE = 'w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm font-semibold text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/40 focus:border-[#c4a77d]/40 transition-all';
// Neues Design (README §4): Label 12/700 --ink3, Feld 50 px, Radius 15, Fläche --bg mit Linie
const LABEL_IMMO = 'text-[12px] font-bold text-[var(--ink3)] mb-1.5 block';
const INPUT_IMMO = 'w-full min-h-[50px] px-[14px] py-3 rounded-[15px] bg-[var(--bg)] text-[15px] font-bold text-[var(--ink)] placeholder:text-[var(--ink3)] outline-none shadow-[inset_0_0_0_1px_var(--line)] focus:shadow-[inset_0_0_0_2px_var(--acc)] transition-all';

const TerminFormular: React.FC<TerminFormularProps> = ({
  editTermin, prefillDate, teamMembers, userProfile, onClose, onSaved, variant = 'modal', ticketId, prefillObjektId, prefillTitel, prefillBeschreibung, prefillEnde, prefillTyp, prefillMieterId, prefillEigentuemerId, prefillDienstleisterId,
}) => {
  const isEdit = !!editTermin;
  const pille = variant === 'pille';
  const immo = variant === 'immo';
  /** Klassen je Variante: Pille (dunkel), Modal (hell), immo (neues Design mit Tokens). */
  const w = (pilleCls: string, modalCls: string, immoCls: string) => (immo ? immoCls : pille ? pilleCls : modalCls);
  const labelCls = w(LABEL_PILLE, LABEL_MODAL, LABEL_IMMO);
  const inputCls = w(INPUT_PILLE, `${INPUT_MODAL} bg-white`, INPUT_IMMO);
  const dateCls = w(`${INPUT_PILLE} [color-scheme:dark]`, INPUT_MODAL, INPUT_IMMO);
  // Native Dropdown-Panels sind hell — Optionen immer dunkel beschriften.
  // immo: Farbe erbt vom Farbschema (hell/dunkel), sonst wären Optionen im dunklen Menü unlesbar
  const optionCls = immo ? '' : 'text-gray-900';
  const chipCls = w(
    'flex items-center justify-between px-4 py-3 bg-white/5 rounded-xl border border-[#c4a77d]/40',
    'flex items-center justify-between px-4 py-3 bg-gray-50 rounded-xl border border-gray-200',
    'flex items-center justify-between px-4 py-3 rounded-[15px] bg-[var(--acc-soft)]');
  const chipTextCls = w('text-sm font-semibold text-white', 'text-sm font-semibold text-gray-800', 'text-sm font-bold text-[var(--ink)]');
  const searchInputCls = w(
    'w-full pl-9 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm font-semibold text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/40 transition-all',
    'w-full pl-9 pr-4 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/30 focus:border-[#c4a77d] transition-all',
    `${INPUT_IMMO} pl-9`);
  const searchIconCls = w('absolute left-3 top-1/2 -translate-y-1/2 text-white/30', 'absolute left-3 top-1/2 -translate-y-1/2 text-gray-300', 'absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink3)]');
  const entfernenCls = w('text-white/30 hover:text-red-400 transition-colors', 'text-gray-400 hover:text-red-400 transition-colors', 'text-[var(--ink3)] hover:text-[var(--bad)] transition-colors');

  // Gespeichert sind echte Zeitpunkte (timestamptz) — fürs Formular in lokale
  // Wanduhrzeit zurückrechnen. Nur Ganztägig-Termine tragen ihr Datum als
  // UTC-Mitternacht-Konvention, dort zählt der rohe Datumsanteil.
  const getInitialDate = () => {
    if (editTermin) return editTermin.ganztaegig ? editTermin.start_datum.substring(0, 10) : isoZuLokalDatum(editTermin.start_datum);
    if (prefillDate) return prefillDate.substring(0, 10);
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  const getInitialTime = () => {
    if (editTermin && !editTermin.ganztaegig) {
      return isoZuLokalZeit(editTermin.start_datum);
    }
    if (prefillDate && prefillDate.includes('T')) {
      return prefillDate.substring(11, 16);
    }
    return '09:00';
  };

  const [titel, setTitel] = useState(editTermin?.titel || prefillTitel || '');
  const [typ, setTyp] = useState(editTermin?.typ || prefillTyp || 'besichtigung');
  const [ganztaegig, setGanztaegig] = useState(editTermin?.ganztaegig ?? false);
  const [startDate, setStartDate] = useState(getInitialDate());
  const [startTime, setStartTime] = useState(getInitialTime());
  const [endDate, setEndDate] = useState(
    editTermin?.end_datum ? (editTermin.ganztaegig ? editTermin.end_datum.substring(0, 10) : isoZuLokalDatum(editTermin.end_datum)) : (!editTermin && prefillEnde ? prefillEnde.substring(0, 10) : '')
  );
  const [endTime, setEndTime] = useState(
    editTermin?.end_datum && !editTermin.ganztaegig ? isoZuLokalZeit(editTermin.end_datum) : (!editTermin && prefillEnde?.includes('T') ? prefillEnde.substring(11, 16) : '10:00')
  );
  const [beschreibung, setBeschreibung] = useState(editTermin?.beschreibung || prefillBeschreibung || '');
  // Eigene Bezeichnung, nur bei „Sonstiges" — der Sammelbegriff selbst sagt niemandem etwas.
  const [typFreitext, setTypFreitext] = useState(editTermin?.typ_freitext || '');
  const [status, setStatus] = useState(editTermin?.status || 'geplant');
  const [zustaendigId, setZustaendigId] = useState(editTermin?.zustaendig_id || '');
  const [objektId, setObjektId] = useState(editTermin?.objekt_id || prefillObjektId || '');
  const [mieterId, setMieterId] = useState(editTermin?.mieter_id || prefillMieterId || '');
  const [eigentuemerId, setEigentuemerId] = useState(editTermin?.eigentuemer_id || prefillEigentuemerId || '');

  // Teilnehmer = Meeting-Einladungen: der Outlook-Sync exportiert sie als attendees,
  // Exchange verschickt die Einladungs-Mails; der Antwort-Status kommt per Import zurück.
  const [teilnehmer, setTeilnehmer] = useState<TerminTeilnehmer[]>(editTermin?.teilnehmer || []);
  const [teilnehmerSearch, setTeilnehmerSearch] = useState('');
  const [teilnehmerResults, setTeilnehmerResults] = useState<{ email: string; name: string; typ?: string }[]>([]);

  const [objektSearch, setObjektSearch] = useState('');
  const [mieterSearch, setMieterSearch] = useState('');
  const [eigentuemerSearch, setEigentuemerSearch] = useState('');

  const [objektResults, setObjektResults] = useState<any[]>([]);
  const [mieterResults, setMieterResults] = useState<any[]>([]);
  const [eigentuemerResults, setEigentuemerResults] = useState<any[]>([]);

  const [selectedObjekt, setSelectedObjekt] = useState<any>(editTermin?.objekt || null);
  const [selectedMieter, setSelectedMieter] = useState<any>(editTermin?.mieter_rel || null);
  const [selectedEigentuemer, setSelectedEigentuemer] = useState<any>(editTermin?.eigentuemer_rel || null);

  const [saving, setSaving] = useState(false);
  // Mieterportal (Portale E5): Häkchen + Hinweis. Beim Bearbeiten frisch laden – manche Aufrufer
  // übergeben Termine ohne diese Spalten; gespeichert wird der Portal-Stand nur, wenn er bekannt ist.
  const [portalSichtbar, setPortalSichtbar] = useState(false);
  const [mieterHinweis, setMieterHinweis] = useState('');
  const [portalBekannt, setPortalBekannt] = useState(!editTermin?.id);
  // Eigentümerportal (Portale E7): eigenes Häkchen, nichts automatisch
  const [eigPortalSichtbar, setEigPortalSichtbar] = useState(false);
  useEffect(() => {
    if (!editTermin?.id) return;
    let aktiv = true;
    void supabase.from('termine').select('fuer_mieter_sichtbar, mieter_hinweis, fuer_eigentuemer_sichtbar').eq('id', editTermin.id).maybeSingle()
      .then(({ data, error }) => {
        if (!aktiv || error || !data) return;
        setPortalSichtbar(data.fuer_mieter_sichtbar === true);
        setMieterHinweis((data.mieter_hinweis as string | null) ?? '');
        setEigPortalSichtbar(data.fuer_eigentuemer_sichtbar === true);
        setPortalBekannt(true);
      });
    return () => { aktiv = false; };
  }, [editTermin?.id]);
  const { openForm, closeForm } = useIslandActions();
  // Pille: Meldungen in der Fehlerleiste statt alert (Modal/immo unverändert mit alert).
  const fehlerPille = useFormFehler();
  const melde = (text: string) => { if (pille) fehlerPille.zeige(text); else alert(text); };

  // Verdeckte Anfrage an mehrere: Eine Outlook-Einladung kennt kein Bcc — alle Eingeladenen
  // sehen sich dort. Wer mehrere Handwerker nach derselben Zeit fragt, verschickt deshalb
  // eine normale Mail mit allen im Bcc (User-Wunsch 2026-08-12). Die Teilnehmerliste des
  // Termins bleibt unangetastet.
  const anfragePerMail = async () => {
    const start = ganztaegig ? `${startDate}T00:00:00Z` : lokalZuIso(startDate, startTime);
    const ende = endDate ? (ganztaegig ? `${endDate}T23:59:59Z` : lokalZuIso(endDate, endTime)) : null;
    const daten = {
      titel, beschreibung, start_datum: start, end_datum: ende, ganztaegig,
      objekt: selectedObjekt || null,
    };
    const { data: konten } = await supabase
      .from('email_konten').select('id, email_adresse, bezeichnung, absender_name')
      .eq('aktiv', true).order('ist_standard', { ascending: false }).limit(1);
    const konto = konten?.[0];
    if (!konto) { melde('Kein aktives E-Mail-Postfach gefunden.'); return; }
    openForm(
      <EmailVerfassen
        variant="pille"
        konto={{ id: konto.id, email_adresse: konto.email_adresse, bezeichnung: konto.bezeichnung || konto.absender_name || konto.email_adresse }}
        autoVorlageTrigger="termin_einladung"
        autoVorlageWerte={terminAnfragePlatzhalter(daten)}
        autoText={anfrageText(daten)}
        draft={{ an: [], bcc: teilnehmer.map((t) => t.email), betreff: anfrageBetreff(daten), text_inhalt: '' }}
        onClose={() => closeForm()}
        onSent={() => closeForm()}
      />,
      'Terminanfrage (Bcc)',
      640, MAIL_FORM_META,
    );
  };

  // Vorbelegte Verknüpfungen (Objekt, Mieter) als Auswahl anzeigen – sonst steht nur die ID im Hintergrund
  useEffect(() => {
    if (!objektId || selectedObjekt) return;
    let aktiv = true;
    supabase.from('objekte').select('id, bezeichnung, strasse, hausnummer').eq('id', objektId).maybeSingle()
      .then(({ data }) => { if (aktiv && data) setSelectedObjekt(data); });
    return () => { aktiv = false; };
  }, [objektId, selectedObjekt]);
  useEffect(() => {
    if (!eigentuemerId || selectedEigentuemer) return;
    let aktiv = true;
    supabase.from('eigentuemer').select('id, vorname, nachname, firma_name').eq('id', eigentuemerId).maybeSingle()
      .then(({ data }) => { if (aktiv && data) setSelectedEigentuemer(data); });
    return () => { aktiv = false; };
  }, [eigentuemerId, selectedEigentuemer]);
  useEffect(() => {
    if (!mieterId || selectedMieter) return;
    let aktiv = true;
    supabase.from('mieter').select('id, vorname, nachname').eq('id', mieterId).maybeSingle()
      .then(({ data }) => { if (aktiv && data) setSelectedMieter(data); });
    return () => { aktiv = false; };
  }, [mieterId, selectedMieter]);

  // Typeahead searches
  useEffect(() => {
    if (objektSearch.length < 2) { setObjektResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await supabase.from('objekte').select('id, bezeichnung, strasse, hausnummer').ilike('bezeichnung', `%${objektSearch}%`).limit(5);
      setObjektResults(data || []);
    }, 300);
    return () => clearTimeout(t);
  }, [objektSearch]);

  useEffect(() => {
    if (mieterSearch.length < 2) { setMieterResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await supabase.from('mieter').select('id, vorname, nachname').or(`vorname.ilike.%${mieterSearch}%,nachname.ilike.%${mieterSearch}%`).limit(5);
      setMieterResults(data || []);
    }, 300);
    return () => clearTimeout(t);
  }, [mieterSearch]);

  // Teilnehmer-Typeahead über alle Kontakte mit E-Mail (Mieter/Eigentümer/Dienstleister)
  // Teilnehmer-Suche über ALLE Personenquellen (auch sonstige Kontakte und Mitarbeitende) —
  // vorher fehlten genau diese beiden, die eigene Kollegin war nicht auffindbar.
  useEffect(() => {
    if (teilnehmerSearch.length < 2) { setTeilnehmerResults([]); return; }
    const t = setTimeout(async () => {
      const treffer = await sucheEmpfaenger(teilnehmerSearch, 4);
      setTeilnehmerResults(treffer.slice(0, 8).map((k) => ({ email: k.email, name: k.name, typ: k.typ })));
    }, 300);
    return () => clearTimeout(t);
  }, [teilnehmerSearch]);

  const addTeilnehmer = (email: string, name?: string | null) => {
    const mail = email.trim();
    if (!mail || teilnehmer.some(p => p.email.toLowerCase() === mail.toLowerCase())) {
      setTeilnehmerSearch(''); setTeilnehmerResults([]); return;
    }
    // Antwort-Status behalten, falls dieselbe Adresse in diesem Edit entfernt + neu hinzugefügt wurde
    const alt = (editTermin?.teilnehmer || []).find(p => p.email.toLowerCase() === mail.toLowerCase());
    setTeilnehmer([...teilnehmer, { email: mail, name: name || alt?.name || null, status: alt?.status || 'none' }]);
    setTeilnehmerSearch(''); setTeilnehmerResults([]);
  };

  const teilnehmerEingabeIstEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(teilnehmerSearch.trim());

  useEffect(() => {
    if (eigentuemerSearch.length < 2) { setEigentuemerResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await supabase.from('eigentuemer').select('id, vorname, nachname, firma_name').or(`vorname.ilike.%${eigentuemerSearch}%,nachname.ilike.%${eigentuemerSearch}%,firma_name.ilike.%${eigentuemerSearch}%`).limit(5);
      setEigentuemerResults(data || []);
    }, 300);
    return () => clearTimeout(t);
  }, [eigentuemerSearch]);

  const handleSave = async () => {
    if (!titel.trim()) return;
    setSaving(true);
    fehlerPille.leeren();

    try {
      // Zeit-Termine: lokale Eingabe → echter Zeitpunkt (sonst landet die Wanduhrzeit
      // als UTC in der timestamptz-Spalte und die Anzeige verschiebt um +1/+2h).
      // Ganztägig: UTC-Mitternacht-Konvention beibehalten — der Outlook-Export
      // schneidet den Datumsanteil roh ab (mapping.ts slice(0,10)).
      const startDatum = ganztaegig ? `${startDate}T00:00:00Z` : lokalZuIso(startDate, startTime);
      let endDatum: string | null = null;
      if (endDate) {
        endDatum = ganztaegig ? `${endDate}T23:59:59Z` : lokalZuIso(endDate, endTime);
      }

      const payload = {
        titel: titel.trim(),
        typ,
        typ_freitext: typ === 'sonstig' ? (typFreitext.trim() || null) : null,
        ganztaegig,
        start_datum: startDatum,
        end_datum: endDatum,
        beschreibung: beschreibung.trim() || null,
        status,
        objekt_id: objektId || null,
        mieter_id: mieterId || null,
        eigentuemer_id: eigentuemerId || null,
        // nur setzen, nie leeren – Bearbeiten aus dem Kalender entfernt keine Dienstleister-Verknüpfung
        ...(prefillDienstleisterId ? { dienstleister_id: prefillDienstleisterId } : {}),
        zustaendig_id: zustaendigId || null,
        teilnehmer,
        ...(portalBekannt ? { fuer_mieter_sichtbar: portalSichtbar, mieter_hinweis: mieterHinweis.trim() || null, fuer_eigentuemer_sichtbar: eigPortalSichtbar } : {}),
        // updated_at manuell stempeln — termine hat KEINEN update_updated_at-Trigger;
        // der Outlook-Sync erkennt App-Änderungen nur über updated_at > sync_am.
        ...(isEdit ? { updated_at: new Date().toISOString() } : { created_by: userProfile?.id }),
      };

      if (isEdit) {
        const { error } = await supabase.from('termine').update(payload).eq('id', editTermin!.id);
        if (error) { console.error('Update error:', error); melde(`Fehler: ${error.message}`); return; }
        // Hängt der Termin an einem Ticket, muss die Änderung dort sichtbar werden —
        // sonst wartet ein Ticket, ohne dass der Grund im Verlauf steht.
        const ticket = await ladeTicketZuTermin(editTermin!.id);
        if (ticket) {
          const verschoben = editTermin!.start_datum !== startDatum;
          const abgesagt = status === 'abgesagt' && editTermin!.status !== 'abgesagt';
          const zeit = terminZeitText({ start_datum: startDatum, end_datum: endDatum, ganztaegig });
          if (abgesagt) {
            await protokolliereTermin(ticket.id, 'termin_abgesagt', `Termin abgesagt: ${titel.trim()} (${zeit})`, userProfile?.id);
          } else if (verschoben) {
            await protokolliereTermin(ticket.id, 'termin_verschoben', `Termin verschoben: ${titel.trim()} → ${zeit}`, userProfile?.id);
          }
        }
      } else {
        const { data: neu, error } = await supabase.from('termine').insert(payload).select('id').single();
        if (error) { console.error('Insert error:', error); melde(`Fehler: ${error.message}`); return; }
        if (ticketId && neu?.id) {
          try {
            await verknuepfeTicketTermin(ticketId, neu.id as string);
            const zeit = terminZeitText({ start_datum: startDatum, end_datum: endDatum, ganztaegig });
            await protokolliereTermin(ticketId, 'termin_angelegt', `Termin angelegt: ${titel.trim()} (${zeit})`, userProfile?.id);
          } catch (e) {
            alert(`Termin gespeichert, Verknüpfung zum Ticket fehlgeschlagen: ${(e as Error).message}`);
          }
        }
      }

      onSaved();
    } catch (err: any) {
      console.error('Fehler beim Speichern:', err);
      melde(`Unerwarteter Fehler: ${err?.message || err}`);
    } finally {
      setSaving(false);
    }
  };

  const dropdownPanelCls = immo
    ? 'absolute top-full mt-1 left-0 right-0 rounded-[15px] z-10 overflow-hidden bg-[var(--bg)] shadow-[0_12px_30px_-8px_var(--shade),inset_0_0_0_1px_var(--line)]'
    : 'absolute top-full mt-1 left-0 right-0 bg-white rounded-xl border border-gray-200 shadow-xl z-10 overflow-hidden';
  const dropdownItemCls = immo
    ? 'w-full px-4 py-2.5 text-left hover:bg-[var(--hover)] transition-colors text-sm font-semibold text-[var(--ink)] border-b border-[var(--line)] last:border-0'
    : 'w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm font-semibold text-gray-700 border-b border-gray-50 last:border-0';

  const inhalt = (
    <div className={w('p-4 space-y-5', 'flex-1 overflow-y-auto p-6 space-y-5 custom-scrollbar', 'space-y-5')}>
      {/* Titel */}
      <div>
        <label className={labelCls}>Titel *</label>
        <input
          type="text"
          value={titel}
          onChange={(e) => setTitel(e.target.value)}
          placeholder="z.B. Besichtigung Musterstraße 5"
          className={inputCls}
        />
      </div>

      {/* Typ + Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Typ</label>
          <select value={typ} onChange={(e) => setTyp(e.target.value)} className={inputCls}>
            {TYP_OPTIONS.map(o => (
              <option key={o.value} value={o.value} className={optionCls}>{o.label}</option>
            ))}
            {typ === 'outlook' && <option value="outlook" className={optionCls}>Outlook</option>}
          </select>
          {typ === 'sonstig' && (
            <input
              type="text"
              value={typFreitext}
              onChange={(e) => setTypFreitext(e.target.value)}
              placeholder="Eigene Bezeichnung, z. B. Übergabe"
              maxLength={40}
              className={`${inputCls} mt-2`}
            />
          )}
        </div>
        <div>
          <label className={labelCls}>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
            {STATUS_OPTIONS.map(o => (
              <option key={o.value} value={o.value} className={optionCls}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Ganztägig */}
      <label className="flex items-center gap-3 cursor-pointer">
        <div className={`w-10 h-5 rounded-full transition-all ${ganztaegig ? (immo ? 'bg-[var(--ok)]' : 'bg-[#c4a77d]') : w('bg-white/20', 'bg-gray-200', 'bg-[var(--fill2)]')}`} onClick={() => setGanztaegig(!ganztaegig)}>
          <div className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-transform ${ganztaegig ? 'translate-x-5' : 'translate-x-0'}`} />
        </div>
        <span className={w('text-[10px] font-black text-white/50 uppercase tracking-widest', 'text-[10px] font-black text-gray-500 uppercase tracking-widest', 'text-[14px] font-bold text-[var(--ink)]')}>Ganztägig</span>
      </label>

      {/* Datum + Zeit */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className={`${labelCls} flex items-center gap-1.5`}>
            <Calendar size={10} /> Start
          </label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={dateCls} />
        </div>
        {!ganztaegig && (
          <div>
            <label className={`${labelCls} flex items-center gap-1.5`}>
              <Clock size={10} /> Uhrzeit
            </label>
            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className={dateCls} />
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className={`${labelCls} flex items-center gap-1.5`}>
            <Calendar size={10} /> Ende (optional)
          </label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={dateCls} />
        </div>
        {!ganztaegig && endDate && (
          <div>
            <label className={`${labelCls} flex items-center gap-1.5`}>
              <Clock size={10} /> Endzeit
            </label>
            <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className={dateCls} />
          </div>
        )}
      </div>

      {/* Beschreibung */}
      <div>
        <label className={labelCls}>Beschreibung</label>
        <textarea
          value={beschreibung}
          onChange={(e) => setBeschreibung(e.target.value)}
          rows={3}
          placeholder="Optionale Beschreibung..."
          className={`${inputCls} resize-none`}
        />
      </div>

      {/* Teilnehmer (Meeting-Einladungen via Outlook) */}
      <div>
        <label className={`${labelCls} flex items-center gap-1.5`}>
          <Mail size={10} /> Teilnehmer einladen
        </label>
        {teilnehmer.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {teilnehmer.map(p => (
              <div key={p.email} className={w(
                'flex items-center gap-2 pl-3 pr-2 py-1.5 bg-white/5 rounded-full border border-[#c4a77d]/40',
                'flex items-center gap-2 pl-3 pr-2 py-1.5 bg-gray-50 rounded-full border border-gray-200',
                'flex items-center gap-2 pl-3 pr-2 py-1.5 bg-[var(--fill2)] rounded-full')}>
                <span className={w('text-xs font-semibold text-white', 'text-xs font-semibold text-gray-800', 'text-xs font-bold text-[var(--ink)]')}>
                  {p.name || p.email}
                </span>
                {p.status === 'accepted' && <span className="text-[10px] font-black text-emerald-400">✓</span>}
                {p.status === 'declined' && <span className="text-[10px] font-black text-red-400">✗</span>}
                {p.status === 'tentative' && <span className="text-[10px] font-black text-amber-400">~</span>}
                <button onClick={() => setTeilnehmer(teilnehmer.filter(x => x.email !== p.email))} className={entfernenCls}>
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="relative">
          <div className={searchIconCls}><Search size={14} /></div>
          <input
            type="text"
            value={teilnehmerSearch}
            onChange={(e) => setTeilnehmerSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && teilnehmerEingabeIstEmail) { e.preventDefault(); addTeilnehmer(teilnehmerSearch); }
            }}
            placeholder="Kontakt suchen oder E-Mail eingeben..."
            className={searchInputCls}
          />
          {(teilnehmerResults.length > 0 || teilnehmerEingabeIstEmail) && (
            <div className={dropdownPanelCls}>
              {teilnehmerResults.map(k => (
                <button key={k.email} onClick={() => addTeilnehmer(k.email, k.name)} className={dropdownItemCls}>
                  {k.name || k.email} <span className="text-gray-400 text-xs">— {k.email}</span>
                  {k.typ && <span className="ml-1.5 text-[10px] font-black uppercase tracking-widest text-[#c4a77d]">{k.typ}</span>}
                </button>
              ))}
              {teilnehmerEingabeIstEmail && !teilnehmerResults.some(k => k.email.toLowerCase() === teilnehmerSearch.trim().toLowerCase()) && (
                <button onClick={() => addTeilnehmer(teilnehmerSearch)} className={dropdownItemCls}>
                  <span className="text-[#c4a77d]">+ Einladen:</span> {teilnehmerSearch.trim()}
                </button>
              )}
            </div>
          )}
        </div>
        <p className={w('mt-1.5 text-[10px] text-white/30', 'mt-1.5 text-[10px] text-gray-400', 'mt-1.5 text-[12px] text-[var(--ink3)]')}>
          Einladung wird beim nächsten Sync per E-Mail von info@immo-schwede.de verschickt —
          die Eingeladenen sehen sich dabei gegenseitig.
        </p>
        <button
          type="button"
          onClick={anfragePerMail}
          className={w(
            'mt-2 flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-white/70 hover:text-white transition-colors',
            'mt-2 flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-gray-500 hover:text-[#c4a77d] transition-colors',
            'mt-2 flex items-center gap-1.5 rounded-[14px] h-9 px-3 bg-[var(--fill2)] text-[13px] font-bold text-[var(--ink)] hover:bg-[var(--hover)] transition-colors')}
        >
          <Mail size={12} /> Anfrage per Mail (Bcc)
        </button>
      </div>

      {/* Zuständig */}
      <div>
        <label className={labelCls}>Zuständig</label>
        <select value={zustaendigId} onChange={(e) => setZustaendigId(e.target.value)} className={inputCls}>
          <option value="" className={optionCls}>— Nicht zugewiesen —</option>
          {teamMembers.map(m => (
            <option key={m.id} value={m.id} className={optionCls}>{m.full_name}</option>
          ))}
        </select>
      </div>

      {/* Objekt Typeahead */}
      <div>
        <label className={`${labelCls} flex items-center gap-1.5`}>
          <Building2 size={10} /> Objekt
        </label>
        {selectedObjekt ? (
          <div className={chipCls}>
            <span className={chipTextCls}>{selectedObjekt.bezeichnung}</span>
            <button onClick={() => { setSelectedObjekt(null); setObjektId(''); }} className={entfernenCls}>
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="relative">
            <div className={searchIconCls}><Search size={14} /></div>
            <input
              type="text"
              value={objektSearch}
              onChange={(e) => setObjektSearch(e.target.value)}
              placeholder="Objekt suchen..."
              className={searchInputCls}
            />
            {objektResults.length > 0 && (
              <div className={dropdownPanelCls}>
                {objektResults.map(o => (
                  <button key={o.id} onClick={() => { setObjektId(o.id); setSelectedObjekt(o); setObjektSearch(''); setObjektResults([]); }} className={dropdownItemCls}>
                    {o.bezeichnung} <span className="text-gray-400 text-xs">— {o.strasse} {o.hausnummer}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mieter Typeahead */}
      <div>
        <label className={`${labelCls} flex items-center gap-1.5`}>
          <Users size={10} /> Mieter
        </label>
        {selectedMieter ? (
          <div className={chipCls}>
            <span className={chipTextCls}>{selectedMieter.vorname} {selectedMieter.nachname}</span>
            <button onClick={() => { setSelectedMieter(null); setMieterId(''); }} className={entfernenCls}>
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="relative">
            <div className={searchIconCls}><Search size={14} /></div>
            <input
              type="text"
              value={mieterSearch}
              onChange={(e) => setMieterSearch(e.target.value)}
              placeholder="Mieter suchen..."
              className={searchInputCls}
            />
            {mieterResults.length > 0 && (
              <div className={dropdownPanelCls}>
                {mieterResults.map(mm => (
                  <button key={mm.id} onClick={() => { setMieterId(mm.id); setSelectedMieter(mm); setMieterSearch(''); setMieterResults([]); }} className={dropdownItemCls}>
                    {mm.vorname} {mm.nachname}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Eigentümer Typeahead */}
      <div>
        <label className={`${labelCls} flex items-center gap-1.5`}>
          <UserCircle size={10} /> Eigentümer
        </label>
        {selectedEigentuemer ? (
          <div className={chipCls}>
            <span className={chipTextCls}>
              {selectedEigentuemer.firma_name || `${selectedEigentuemer.vorname} ${selectedEigentuemer.nachname}`}
            </span>
            <button onClick={() => { setSelectedEigentuemer(null); setEigentuemerId(''); }} className={entfernenCls}>
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="relative">
            <div className={searchIconCls}><Search size={14} /></div>
            <input
              type="text"
              value={eigentuemerSearch}
              onChange={(e) => setEigentuemerSearch(e.target.value)}
              placeholder="Eigentümer suchen..."
              className={searchInputCls}
            />
            {eigentuemerResults.length > 0 && (
              <div className={dropdownPanelCls}>
                {eigentuemerResults.map(e => (
                  <button key={e.id} onClick={() => { setEigentuemerId(e.id); setSelectedEigentuemer(e); setEigentuemerSearch(''); setEigentuemerResults([]); }} className={dropdownItemCls}>
                    {e.firma_name || `${e.vorname} ${e.nachname}`}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mieterportal (Portale E5): Häkchen + Hinweis für den Mieter – wie in der Pillen-Ansicht */}
      <div className="space-y-2">
        <label className={`flex items-center gap-3 ${!portalBekannt || (!mieterId && !objektId) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          data-tip={!mieterId && !objektId ? 'Erst einen Mieter oder ein Objekt zuordnen' : 'Im Mieterportal zeigen – der Mieter sieht Datum, Uhrzeit, Typ und den Hinweis'}>
          <div role="switch" aria-checked={portalSichtbar} aria-label="Im Mieterportal zeigen" tabIndex={0}
            className={`w-10 h-5 rounded-full transition-all ${portalSichtbar ? (immo ? 'bg-[var(--ok)]' : 'bg-[#c4a77d]') : w('bg-white/20', 'bg-gray-200', 'bg-[var(--fill2)]')}`}
            onClick={() => { if (portalBekannt && (mieterId || objektId)) setPortalSichtbar(!portalSichtbar); }}
            onKeyDown={(e) => { if ((e.key === ' ' || e.key === 'Enter') && portalBekannt && (mieterId || objektId)) { e.preventDefault(); setPortalSichtbar(!portalSichtbar); } }}>
            <div className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-transform ${portalSichtbar ? 'translate-x-5' : 'translate-x-0'}`} />
          </div>
          <span className={w('text-[10px] font-black text-white/50 uppercase tracking-widest', 'text-[10px] font-black text-gray-500 uppercase tracking-widest', 'text-[14px] font-bold text-[var(--ink)]')}>Im Mieterportal zeigen</span>
        </label>
        {portalSichtbar && (
          <textarea value={mieterHinweis} onChange={(e) => setMieterHinweis(e.target.value)} rows={2} maxLength={500} aria-label="Hinweis für den Mieter"
            placeholder="Hinweis für den Mieter, z. B. Bitte Zugang zum Bad ermöglichen." className={`${inputCls} resize-none`} />
        )}
        <p className={w('text-[11px] text-white/45', 'text-[11px] text-gray-400', 'text-[12px] text-[var(--ink3)]')}>
          {mieterId
            ? 'Sichtbar nur für den zugeordneten Mieter. Interne Beschreibung und Teilnehmer sieht er nicht. Es wird nichts verschickt.'
            : 'Ohne Mieter: sichtbar für alle Mieter des Objekts (z. B. Rauchmelder-Wartung). Interne Beschreibung und Teilnehmer sehen sie nicht. Es wird nichts verschickt.'}
        </p>
      </div>

      {/* Eigentümerportal (Portale E7): eigenes Häkchen */}
      <div className="space-y-2">
        <label className={`flex items-center gap-3 ${!portalBekannt || (!eigentuemerId && !objektId) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          data-tip={!eigentuemerId && !objektId ? 'Erst einen Eigentümer oder ein Objekt zuordnen' : 'Im Eigentümerportal zeigen – der Eigentümer sieht Datum, Uhrzeit, Titel und die ausführende Firma'}>
          <div role="switch" aria-checked={eigPortalSichtbar} aria-label="Im Eigentümerportal zeigen" tabIndex={0}
            className={`w-10 h-5 rounded-full transition-all ${eigPortalSichtbar ? (immo ? 'bg-[var(--ok)]' : 'bg-[#c4a77d]') : w('bg-white/20', 'bg-gray-200', 'bg-[var(--fill2)]')}`}
            onClick={() => { if (portalBekannt && (eigentuemerId || objektId)) setEigPortalSichtbar(!eigPortalSichtbar); }}
            onKeyDown={(e) => { if ((e.key === ' ' || e.key === 'Enter') && portalBekannt && (eigentuemerId || objektId)) { e.preventDefault(); setEigPortalSichtbar(!eigPortalSichtbar); } }}>
            <div className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-transform ${eigPortalSichtbar ? 'translate-x-5' : 'translate-x-0'}`} />
          </div>
          <span className={w('text-[10px] font-black text-white/50 uppercase tracking-widest', 'text-[10px] font-black text-gray-500 uppercase tracking-widest', 'text-[14px] font-bold text-[var(--ink)]')}>Im Eigentümerportal zeigen</span>
        </label>
        <p className={w('text-[11px] text-white/45', 'text-[11px] text-gray-400', 'text-[12px] text-[var(--ink3)]')}>
          {eigentuemerId
            ? 'Sichtbar nur für den zugeordneten Eigentümer. Beschreibung, Hinweis und Teilnehmer sieht er nicht. Es wird nichts verschickt.'
            : 'Ohne Eigentümer: sichtbar für alle Eigentümer des Objekts. Beschreibung, Hinweis und Teilnehmer sehen sie nicht. Es wird nichts verschickt.'}
        </p>
      </div>

      {/* Aktionen (Pille: unten im Inhalt; Modal: eigener Footer) */}
      <div className={pille || immo ? `flex items-center ${immo ? 'justify-end gap-2' : 'justify-between'} pt-2` : 'hidden'}>
        <button onClick={onClose} className={immo
          ? 'h-11 px-5 rounded-[22px] bg-[var(--fill2)] text-[13px] font-bold text-[var(--ink)] hover:bg-[var(--hover)] transition-colors'
          : 'px-5 py-3 text-[10px] font-black text-white/40 uppercase tracking-widest hover:text-white transition-colors'}>
          Abbrechen
        </button>
        <button
          onClick={handleSave}
          disabled={saving || !titel.trim()}
          className={immo
            ? 'flex items-center gap-2 h-11 px-6 rounded-[22px] [background:var(--acc-fill)] text-[var(--acc-ink)] text-[13px] font-extrabold shadow-[var(--acc-sh)] transition-all active:scale-95 disabled:opacity-45 disabled:cursor-not-allowed'
            : 'flex items-center gap-2 px-6 py-3 bg-[#c4a77d] text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:bg-[#b3966c] transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed'}
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {isEdit ? 'Speichern' : 'Erstellen'}
        </button>
      </div>
    </div>
  );

  // Pille (Handoff Pillen-Formulare): Bausteine, gleiche Zustände und Handler wie oben.
  if (pille) {
    const leer = { icon: Minus, iconFlaeche: ZEILEN_ICON.leer.flaeche, iconFarbe: ZEILEN_ICON.leer.farbe };
    const neutral = { iconFlaeche: ZEILEN_ICON.gedaempft.flaeche, iconFarbe: ZEILEN_ICON.gedaempft.farbe };
    const dunkel = ZEILEN_ICON.dunkel.flaeche;
    const TYP_ICON: Record<string, { icon: typeof Eye; farbe: string }> = {
      besichtigung: { icon: Eye, farbe: ZEILEN_ICON.blau.farbe },
      wartung: { icon: Wrench, farbe: CHIP.orange.text },
      frist: { icon: HourglassMedium, farbe: FORM.rot },
      meeting: { icon: UsersThree, farbe: ZEILEN_ICON.gruen.farbe },
      sonstig: { icon: DotsThreeCircle, farbe: ZEILEN_ICON.violett.farbe },
    };
    const typOptionen = [
      ...TYP_OPTIONS.map((o) => ({ wert: o.value, label: o.label, icon: TYP_ICON[o.value].icon, iconFarbe: TYP_ICON[o.value].farbe, iconFlaeche: dunkel })),
      ...(typ === 'outlook' ? [{ wert: 'outlook', label: 'Outlook', icon: CalendarBlank, ...neutral }] : []),
    ];
    // Antwort-Status der Eingeladenen: Icons statt der bisherigen Zeichen ✓ ✗ ~ (gleiche Farben).
    const statusIcon = (status?: string | null) => {
      if (status === 'accepted') return <PhCheck aria-label="Zugesagt" size={12} weight="bold" color={CHIP.gruen.text} />;
      if (status === 'declined') return <PhX aria-label="Abgesagt" size={12} weight="bold" color={FORM.rot} />;
      if (status === 'tentative') return <Question aria-label="Vorbehaltlich" size={12} weight="bold" color={CHIP.orange.text} />;
      return null;
    };
    const einladen = teilnehmerEingabeIstEmail && !teilnehmerResults.some(k => k.email.toLowerCase() === teilnehmerSearch.trim().toLowerCase());
    return (
      <FormRahmen fehler={fehlerPille.text} knopfleiste={
        <FormKnopfleiste abbrechen={{ label: 'Abbrechen', onClick: onClose }}
          haupt={{
            label: isEdit ? 'Speichern' : 'Erstellen', icon: FloppyDisk, typ: 'button', onClick: handleSave, laedt: saving,
            gesperrt: !titel.trim() && !saving, gesperrtGrund: 'Titel eingeben',
          }} />
      }>
        <FormAbschnitt titel="Termin" icon={CalendarPlus} farbe="blau" pflicht={1} index={0}>
          <FormFeld label="Titel" value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="z.B. Besichtigung Musterstraße 5" pflicht />
          <FormAuswahl label="Typ" wert={typ} onChange={setTyp} optionen={typOptionen} tip="Typ – Art des Termins" />
          {typ === 'sonstig' && (
            <FormFeld label="Eigene Bezeichnung" value={typFreitext} onChange={(e) => setTypFreitext(e.target.value)}
              placeholder="Eigene Bezeichnung, z. B. Übergabe" maxLength={40} />
          )}
          <FormSegment label="Status" wert={status} onChange={setStatus} optionen={STATUS_OPTIONS.map((o) => ({ wert: o.value, label: o.label }))} />
        </FormAbschnitt>

        <FormAbschnitt titel="Zeit" icon={PhClock} farbe="orange" index={1}>
          <FormSchalter text="Ganztägig" an={ganztaegig} onChange={setGanztaegig} icon={Sun} verlauf={ABSCHNITT_FARBE.orange.verlauf} />
          <FormZeile>
            <FormFeld label="Start" typ="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            {!ganztaegig && <FormFeld label="Uhrzeit" typ="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />}
          </FormZeile>
          <FormZeile>
            <FormFeld label="Ende (optional)" typ="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            {!ganztaegig && endDate && <FormFeld label="Endzeit" typ="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />}
          </FormZeile>
        </FormAbschnitt>

        <FormAbschnitt titel="Beschreibung" icon={TextAlignLeft} farbe="violett" index={2}>
          <FormFeld label="Beschreibung" value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)} zeilen={3} placeholder="Optionale Beschreibung..." />
        </FormAbschnitt>

        <FormAbschnitt titel="Teilnehmer einladen" icon={EnvelopeSimple} farbe="blau" index={3}>
          <FormChips chips={teilnehmer.map((p) => ({
            key: p.email, label: p.name || p.email, zusatz: statusIcon(p.status), entfernenLabel: 'Einladung entfernen',
            onEntfernen: () => setTeilnehmer(teilnehmer.filter(x => x.email !== p.email)),
          }))} />
          <FormSuche label="Kontakt suchen" platzhalter="Kontakt suchen oder E-Mail eingeben..."
            tip="Kontakt suchen – Mieter, Eigentümer, Dienstleister, Kontakte und Team durchsuchen"
            wert={teilnehmerSearch} onChange={(e) => setTeilnehmerSearch(e.target.value)} offen mindestLaenge={0}
            onSchliessen={() => setTeilnehmerResults([])}
            onKeyDown={(e) => { if (e.key === 'Enter' && teilnehmerEingabeIstEmail) { e.preventDefault(); addTeilnehmer(teilnehmerSearch); } }}
            hinweis="Einladung wird beim nächsten Sync per E-Mail von info@immo-schwede.de verschickt — die Eingeladenen sehen sich dabei gegenseitig."
            treffer={[
              ...teilnehmerResults.map((k) => ({ id: k.email, label: k.name || k.email, unter: k.typ ? `${k.email} · ${k.typ}` : k.email, icon: User, ...neutral })),
              ...(einladen ? [{ id: '__einladen__', label: `+ Einladen: ${teilnehmerSearch.trim()}`, icon: EnvelopeSimple, iconFlaeche: ZEILEN_ICON.gold.flaeche, iconFarbe: ZEILEN_ICON.gold.farbe }] : []),
            ]}
            onWaehlen={(id) => {
              if (id === '__einladen__') { addTeilnehmer(teilnehmerSearch); return; }
              const k = teilnehmerResults.find((x) => x.email === id);
              if (k) addTeilnehmer(k.email, k.name);
            }} />
          <FormKleinKnopf label="Anfrage per Mail (Bcc)" icon={EnvelopeSimple} onClick={() => void anfragePerMail()}
            tip="Anfrage per Mail (Bcc) – Normale Mail mit allen Teilnehmern im Bcc – sie sehen sich gegenseitig nicht" />
        </FormAbschnitt>

        <FormAbschnitt titel="Zuordnung" icon={LinkSimple} farbe="gold" index={4}>
          <FormAuswahl label="Zuständig" wert={zustaendigId} onChange={setZustaendigId} tip="Zuständig – Wer den Termin wahrnimmt"
            optionen={[{ wert: '', label: '— Nicht zugewiesen —', ...leer },
              ...teamMembers.map((m) => ({ wert: m.id, label: m.full_name, icon: User, iconFlaeche: ZEILEN_ICON.gruen.flaeche, iconFarbe: ZEILEN_ICON.gruen.farbe }))]} />
          <FormSuche label="Objekt" platzhalter="Objekt suchen..." tip="Objekt – Objekt, um das es beim Termin geht"
            wert={selectedObjekt ? selectedObjekt.bezeichnung : objektSearch} onChange={(e) => setObjektSearch(e.target.value)}
            offen mindestLaenge={2} festNachAuswahl ausgewaehlt={!!selectedObjekt} onSchliessen={() => setObjektResults([])}
            onEntfernen={() => { setSelectedObjekt(null); setObjektId(''); }}
            treffer={selectedObjekt ? [] : objektResults.map((o) => ({ id: o.id, label: o.bezeichnung, unter: `${o.strasse} ${o.hausnummer}`, icon: Buildings, iconFlaeche: ZEILEN_ICON.gold.flaeche, iconFarbe: ZEILEN_ICON.gold.farbe }))}
            onWaehlen={(id) => { const o = objektResults.find((x) => x.id === id); if (o) { setObjektId(o.id); setSelectedObjekt(o); setObjektSearch(''); setObjektResults([]); } }} />
          <FormSuche label="Mieter" platzhalter="Mieter suchen..."
            wert={selectedMieter ? `${selectedMieter.vorname} ${selectedMieter.nachname}` : mieterSearch} onChange={(e) => setMieterSearch(e.target.value)}
            offen mindestLaenge={2} festNachAuswahl ausgewaehlt={!!selectedMieter} onSchliessen={() => setMieterResults([])}
            onEntfernen={() => { setSelectedMieter(null); setMieterId(''); }}
            treffer={selectedMieter ? [] : mieterResults.map((mm) => ({ id: mm.id, label: `${mm.vorname} ${mm.nachname}`, icon: User, ...neutral }))}
            onWaehlen={(id) => { const mm = mieterResults.find((x) => x.id === id); if (mm) { setMieterId(mm.id); setSelectedMieter(mm); setMieterSearch(''); setMieterResults([]); } }} />
          <FormSuche label="Eigentümer" platzhalter="Eigentümer suchen..."
            wert={selectedEigentuemer ? (selectedEigentuemer.firma_name || `${selectedEigentuemer.vorname} ${selectedEigentuemer.nachname}`) : eigentuemerSearch}
            onChange={(e) => setEigentuemerSearch(e.target.value)}
            offen mindestLaenge={2} festNachAuswahl ausgewaehlt={!!selectedEigentuemer} onSchliessen={() => setEigentuemerResults([])}
            onEntfernen={() => { setSelectedEigentuemer(null); setEigentuemerId(''); }}
            treffer={selectedEigentuemer ? [] : eigentuemerResults.map((e) => ({ id: e.id, label: e.firma_name || `${e.vorname} ${e.nachname}`, icon: PhUserCircle, ...neutral }))}
            onWaehlen={(id) => { const e = eigentuemerResults.find((x) => x.id === id); if (e) { setEigentuemerId(e.id); setSelectedEigentuemer(e); setEigentuemerSearch(''); setEigentuemerResults([]); } }} />
        </FormAbschnitt>
        <FormAbschnitt titel="Portale" icon={Globe} farbe="gruen" index={5}
          tip="Portale – Termin dem Mieter bzw. Eigentümer in seinem Portal zeigen">
          <FormSchalter text="Im Mieterportal zeigen" an={portalSichtbar} onChange={setPortalSichtbar} icon={Globe}
            verlauf={ABSCHNITT_FARBE.gruen.verlauf} gesperrt={!portalBekannt || (!mieterId && !objektId)}
            tip={!mieterId && !objektId ? 'Erst einen Mieter oder ein Objekt zuordnen' : 'Im Mieterportal zeigen – der Mieter sieht Datum, Uhrzeit, Typ und den Hinweis'} />
          {portalSichtbar && (
            <FormFeld label="Hinweis für den Mieter" value={mieterHinweis} onChange={(e) => setMieterHinweis(e.target.value)} zeilen={2} maxLength={500}
              placeholder="z. B. Bitte Zugang zum Bad ermöglichen. Wenn Sie nicht da sind, öffnet der Hausmeister." />
          )}
          <span style={{ fontSize: 12, lineHeight: 1.45, color: 'rgba(255,255,255,0.55)' }}>
            {mieterId
              ? 'Sichtbar nur für den zugeordneten Mieter. Interne Beschreibung und Teilnehmer sieht er nicht. Es wird nichts verschickt.'
              : 'Ohne Mieter: sichtbar für alle Mieter des Objekts (z. B. Rauchmelder-Wartung). Interne Beschreibung und Teilnehmer sehen sie nicht. Es wird nichts verschickt.'}
          </span>
          <FormSchalter text="Im Eigentümerportal zeigen" an={eigPortalSichtbar} onChange={setEigPortalSichtbar} icon={Globe}
            verlauf={ABSCHNITT_FARBE.gruen.verlauf} gesperrt={!portalBekannt || (!eigentuemerId && !objektId)}
            tip={!eigentuemerId && !objektId ? 'Erst einen Eigentümer oder ein Objekt zuordnen' : 'Im Eigentümerportal zeigen – der Eigentümer sieht Datum, Uhrzeit, Titel und die ausführende Firma'} />
          <span style={{ fontSize: 12, lineHeight: 1.45, color: 'rgba(255,255,255,0.55)' }}>
            {eigentuemerId
              ? 'Sichtbar nur für den zugeordneten Eigentümer. Beschreibung, Hinweis und Teilnehmer sieht er nicht. Es wird nichts verschickt.'
              : 'Ohne Eigentümer: sichtbar für alle Eigentümer des Objekts. Beschreibung, Hinweis und Teilnehmer sehen sie nicht. Es wird nichts verschickt.'}
          </span>
        </FormAbschnitt>
      </FormRahmen>
    );
  }

  // immo: nackter Inhalt im neuen Immobilien-Design.
  if (immo) return inhalt;

  return (
    <div className="fixed inset-0 z-[250] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-termin-modal>
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0f172a] to-[#1e293b] p-6 text-white">
          <div className="flex justify-between items-center">
            <div className="space-y-1">
              <h2 className="text-lg font-black uppercase tracking-tight">{isEdit ? 'Termin bearbeiten' : 'Neuer Termin'}</h2>
              <p className="text-[9px] font-black text-white/50 uppercase tracking-[0.2em]">
                {isEdit ? 'Änderungen vornehmen' : 'Termin erstellen'}
              </p>
            </div>
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        {inhalt}

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 flex items-center justify-between">
          <button onClick={onClose} className="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors">
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !titel.trim()}
            className="flex items-center gap-2 px-6 py-3 bg-[#0f172a] text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:bg-black transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {isEdit ? 'Speichern' : 'Erstellen'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TerminFormular;
